import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { SelectParams } from '@/types/api/select';
import { createCrudHooks } from './create-crud-hooks';

export const {
  keys: artistKeys,
  useList: useArtists,
  useInfiniteList: useInfiniteArtists,
  useDetail: useArtist,
  useCreate: useCreateArtist,
  useUpdate: useUpdateArtist,
  useDelete: useDeleteArtist,
} = createCrudHooks(apiClient['artists'], 'artists');

export type ArtistOption = { id: string; name: string };

/**
 * Opções para o `AsyncSelect` de artista. Artista inativo **não** aparece na lista; só se já
 * for o valor selecionado (`selectedId`), para o campo continuar mostrando o nome ao editar.
 */
export const createArtistOptionsQuery = (selectedId?: string | null) => {
  return (params?: SelectParams) =>
    useQuery<ArtistOption[], Error>({
      queryKey: ['artists', 'options', params?.search ?? '', selectedId ?? null],
      queryFn: async () => {
        const res = await apiClient.artists.get({
          query: {
            search: params?.search || undefined,
            isActive: 'true',
            orderBy: 'name',
            order: 'asc',
            perPage: 30,
          },
        });
        const options: ArtistOption[] = (res.data?.data ?? []).map(({ id, name }) => ({
          id,
          name,
        }));

        if (selectedId && !options.some((option) => option.id === selectedId)) {
          const selected = await apiClient.artists({ id: selectedId }).get();
          if (selected.data) {
            options.unshift({ id: selected.data.id, name: selected.data.name });
          }
        }

        return options;
      },
      staleTime: 30_000,
    });
};
